<template>
  <div class="g-jpca-mobile-navigation" data-testid="g-mobile-navigation">
    <button
      ref="toggle"
      :class="[
        'g-mobile-navigation__toggle',
        { 'g-mobile-navigation__toggle--active': active }
      ]"
      type="button"
      aria-label="Menu"
      :aria-expanded="active ? 'true' : 'false'"
      @click="toggleNav"
    >
      <span class="g-mobile-navigation__hamburger">
        <span></span>
      </span>
    </button>

    <Modal
      :id="id"
      :key="id"
      :a11y-heading="modalHeading"
      :class="[
        'g-mobile-navigation',
        { 'g-mobile-navigation--active': activeClass }
      ]"
      :show-close-button="false"
      :focus-selector="initialFocusElement"
      @after-open="setClass"
      @after-close="closeModal"
    >
      <!-- removed modal close toggle -->

      <div class="g-header__container">
        <div class="g-header__inner gui-container">
          <div class="g-header__logo">
            <a :href="baseUrl" aria-label="Getty">
              <JPCAHeaderLogo :modifier="modifier" />
            </a>
          </div>

          <!-- removed search button -->
        </div>
      </div>

      <div class="g-mobile-navigation__inner">
        <nav
          class="g-mobile-navigation__nav g-mobile-navigation__nav--primary"
          :aria-label="navAriaLabel"
        >
          <h2 id="mobileNavPrimary" class="visually-hidden">
            Mobile Primary Navigation
          </h2>
          <nav
            class="g-header__base-nav"
            role="navigation"
            :aria-label="baseNavAriaLabel"
          >
            <ListOfLinks :links="baseNav" />
          </nav>
          <ul aria-labelledby="mobileNavPrimary">
            <template v-for="(item, index) in primaryItems" :key="index">
              <li>
                <GuiLink v-bind="item">
                  {{ item.text }}
                </GuiLink>

                <!-- removed secondary dropdown panel -->
              </li>
            </template>
          </ul>
        </nav>

        <!-- removed secondary nav -->
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  GuiLink,
  ListOfLinks,
  Modal,
  breakpointsMixin,
  injectBaseUrlMixin,
  isSsr,
  modalControlsMixin,
  useEventBus,
  useLocalization
} from "@thegetty-private/getty-ui";
import JPCAHeaderLogo from "@/components/atoms/jpcaHeaderLogo/index.vue";

export default {
  name: "JPCAMobileNavigation",
  components: {
    GuiLink,
    Modal,
    JPCAHeaderLogo,
    ListOfLinks
  },

  mixins: [breakpointsMixin, injectBaseUrlMixin, modalControlsMixin],

  props: {
    primaryItems: {
      type: Array,
      default: null
    },
    secondaryItems: {
      type: Array,
      default: null
    },
    modifiers: {
      type: Array,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    /**
     * Array of objects to display as the Base header navigation
     */
    baseNav: {
      type: Array,
      default: null
    },
    /**
     * Style variation
     */
    modifier: {
      type: [String, undefined],
      default: undefined,
      validation: (value) => ["light", undefined].includes(value)
    }
  },

  emits: ["searchTriggerMouseover", "searchTriggerMouseout"],

  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },

  data() {
    return {
      active: false,
      activeClass: false,
      eventBus: useEventBus(),
      id: "mobileNavModal",
      isOpen: null,
      modalHeading: this.translatePart("g-jpca-header.modal_aria_label"),
      navAriaLabel: this.translatePart("g-jpca-header.nav_aria_label"),
      baseNavAriaLabel: this.translatePart("g-jpca-header.base_nav_aria_label")
    };
  },

  computed: {
    initialFocusElement() {
      return this.$refs.modalClose;
    }
  },

  watch: {
    "$route.name": {
      handler() {
        this.closeNav();
      }
    }
  },

  mounted() {
    window.addEventListener("resize", this.handleWindowResize);
  },

  unmount() {
    window.removeEventListener("resize", this.handleWindowResize);
  },

  methods: {
    activeLinkAria(url) {
      if (isSsr) return false;

      let isActiveLink =
        window.location.pathname.replace(/\//gi, "") ===
        url.replace(/\//gi, "");

      return isActiveLink ? "page" : null;
    },

    handleWindowResize() {
      if (!this.isSmallMinus() && this.active) {
        this.closeNav();
      }
    },

    openNav() {
      this.triggerModalOpen(this.id);
      this.active = true;
      this.$nextTick(() => {
        setTimeout(() => this.$refs.toggle.focus());
      });
      this.eventBus.emit("mobileNavToggle", true);
    },

    closeNav() {
      this.triggerModalClose(this.id);
    },

    toggleNav() {
      if (this.active) {
        this.closeNav();
      } else {
        this.openNav();
      }
    },

    triggerNavOverlay(id, $event) {
      if (!id) return;

      const overlayId = `navOverlay${id}`;

      this.triggerModalOpen(overlayId, $event);
    },

    togglePanel(index) {
      this.isOpen = this.isOpen !== index ? index : null;
    },

    setClass() {
      setTimeout(() => {
        this.activeClass = this.active;
      }, 0);
    },

    // clean up props on @afterClose event from modal
    closeModal() {
      this.isOpen = null;
      this.active = false;
      this.activeClass = false;
      this.eventBus.emit("mobileNavToggle", false);

      setTimeout(() => {
        this.$refs.toggle.focus();
      }, 5);
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
